import { createFileRoute } from '@tanstack/react-router';
import { usePaginatedQuery, useMutation, useAction, useQuery } from 'convex/react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import ErrorBoundary from '@/components/ui/error-boundary';
import { Badge } from '@/components/ui/badge';
import * as React from 'react';
import { MessagesList } from '@/components/features/messages-list';
import { PlatformFeePaymentButton } from '@/components/features/platform-fee-payment-button';
import { SubstitutePlatformFeeModal } from '@/components/features/substitute-platform-fee-modal';
import { ActivityReportModal } from '@/components/features/substitute/activity-report-modal';
import { RateProviderModal } from '@/components/features/ratings/RateProviderModal';
import { Conversation, ConversationComposer, ConversationContent } from '@/components/ai-elements/conversation';
import { toast } from '@/template/lib/toast';
import { MessageSquare, Lock, CheckCircle2, Star, Loader2, Check, AlertTriangle, Clock, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { WorkInvoiceCard } from '@/components/features/substitute/work-invoice-card';
import { type WorkInvoiceCardData } from '@/types/invoices';
import { type ApplyModalJob, type ApplyForJobEventDetail, type CanApplyResult, type SubstituteFeeStatus } from '@/types/duty';
import { type ChoosableApplicationValidityDays, CHOOSABLE_APPLICATION_VALIDITY_DAYS, DEFAULT_APPLICATION_VALIDITY_DAYS } from '@/types/validators';
import {
  type PublicDutyData,
  type AssignedDutyData,
  type SubstituteApplication,
  type SubstituteThreadGating,
  type SubstituteContract,
  type SubstituteCurrentUser,
  type SubstituteUserProfile,
  type SubstituteRatingCheck,
  type SubstituteExistingRating,
  type SubstituteDocument,
  type DistanceInfo,
} from '@/types/substitute-service';
import type { Id } from 'convex/_generated/dataModel';
import { uploadChatAttachment } from '@/lib/upload-file';
import { EditPatientCountDialog } from '@/components/features/substitute/edit-patient-count-dialog';
import { DocumentsSection } from '@/components/features/substitute/service-detail/documents-section';
import { DocumentsTaskCard } from '@/components/features/substitute/documents-task-card';
import { requiredSettlementDocuments } from '@/lib/settlement-documents';
import { CompletedDutySection } from '@/components/features/substitute/service-detail/completed-duty-section';
import { PublicDutyView, selectNewestApplication } from '@/components/features/substitute/service-detail/public-duty-view';
import { ServiceDetailsCard } from '@/components/features/substitute/service-detail/service-details-card';
import { ContractViewSection } from '@/components/features/contract-view-section';
import { SubstitutePageHero, type SubstituteViewStatus } from '@/components/features/substitute/page-hero';
import { SubstituteProgressStepper } from '@/components/features/substitute/service-progress-stepper';
import { ProviderInfoCard } from '@/components/features/substitute/provider-info-card';
import { SectionCard } from '@/components/features/substitute/section-card';
import { ActivityReportSection } from '@/components/features/activity-report-section';
import { ActivityReportActionCard } from '@/components/features/substitute/activity-report-action-card';
import { Slider } from '@/components/ui/slider';
import { useTranslation } from 'react-i18next';
import { UnavailablePaymentMethodNotice } from '@/components/features/unavailable-payment-method-notice';

export const Route = createFileRoute('/_protected/substitute/$id')({
  loader: async () => {
    const { default: i18n } = await import('@/i18n');
    await i18n.loadNamespaces?.(['duties', 'common']);
    return null;
  },
  component: () => (
    <>
      <SubstituteDetailPage />
      <ApplyModal />
    </>
  ),
});

const fmt = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

const SubstituteDetailPage = () => {
  const { id } = Route.useParams();
  const { t } = useTranslation(['duties', 'common']);

  // DA-431: Read openApply search param for auto-opening apply modal
  // Using URLSearchParams directly to avoid TanStack Router strict type issues
  const openApply = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.get('openApply') === 'true';
  }, []);

  // Fetch public duty information (always available for authenticated users)
  const dutyId = id as Id<'duties'>;
  const publicDuty = useQuery(api.app.duties.queries.getDutyPublic as any, { dutyId }) as PublicDutyData | undefined;

  // Check if user is assigned to this duty
  const myAssigned = (useQuery(api.app.duties.queries.listMyAssignedDuties as any, {}) as AssignedDutyData[] | undefined) ?? [];
  const assignedDuty = myAssigned.find((d) => String(d._id) === String(id));
  const isAssigned = !!assignedDuty;

  // Check if user has applied to this duty
  const myApps = (useQuery(api.app.applications.queries.listMyApplications as any, {}) as SubstituteApplication[] | undefined) ?? [];
  const myApplication = selectNewestApplication(myApps, String(id));
  const hasApplied = !!myApplication;

  // Renewal deep link from the expiry/request email: read once, then clear the hash like `#rate`.
  // The status card only scrolls to and focuses the renew button; it never renews on load.
  const [focusRenew] = React.useState(() => window.location.hash === '#renew');
  React.useEffect(() => {
    if (focusRenew) window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }, [focusRenew]);

  // Check verification status
  const canApplyResult = useQuery(api.app.verification.canApplyToDuties as any) as CanApplyResult | undefined;

  // Use assigned duty data if available, otherwise use public data
  const duty = isAssigned ? assignedDuty : publicDuty;

  const gating = useQuery(api.app.messages.queries.getThreadByService as any, isAssigned ? { dutyId } : 'skip') as SubstituteThreadGating | null | undefined;
  const chatOpen = gating !== null && gating !== undefined && isAssigned;

  // Get representative profile for location
  const myProfile = useQuery(api.app.users.queries.getMyUserProfile as any, {}) as SubstituteUserProfile | undefined;

  const pagination = usePaginatedQuery(api.app.messages.queries.listMessagesByDuty as any, chatOpen ? { dutyId, paginationOpts: { numItems: 20 } } : 'skip', { initialNumItems: chatOpen ? 20 : 1 });
  const send = useMutation(api.app.messages.mutations.sendMessage as any);
  const markRead = useMutation(api.app.messages.mutations.markDutyChatRead as any);
  const createUploadUrl = useMutation(api.app.storage.mutations.createUploadUrl);
  const sendAttachment = useMutation(api.app.storage.mutations.sendAttachment);
  const unread = useQuery(api.app.messages.queries.getDutyChatUnreadCount as any, chatOpen ? { dutyId } : 'skip') as number | undefined;

  // Fetch contract for this duty
  const contract = useQuery(api.app.contracts.getContractByDuty as any, isAssigned ? { dutyId } : 'skip') as SubstituteContract | null | undefined;

  // Complete service mutation
  const completeService = useMutation(api.app.duties.mutations.completeService as any);
  const markPerformed = useMutation(api.app.duties.mutations.markPerformed as any);
  const [isCompleting, setIsCompleting] = React.useState(false);
  const [showWarningDialog, setShowWarningDialog] = React.useState(false);
  const [warningMessage, setWarningMessage] = React.useState('');
  const [showFeeModal, setShowFeeModal] = React.useState(false);
  const [showActivityReportModal, setShowActivityReportModal] = React.useState(false);

  // Chat state for mobile
  const [mobileChatOpen, setMobileChatOpen] = React.useState(false);

  // DA-54: Rating functionality
  const [showRatingModal, setShowRatingModal] = React.useState(false);
  const ratingCheck = useQuery(api.app.ratings.queries.canRateDuty as any, isAssigned ? { dutyId } : 'skip') as SubstituteRatingCheck | undefined;
  const existingRating = useQuery(api.app.ratings.queries.getRatingForDuty as any, isAssigned ? { dutyId } : 'skip') as SubstituteExistingRating | null | undefined;

  // Auto-open rating modal from email deep link (DA-54)
  React.useEffect(() => {
    if (window.location.hash === '#rate' && assignedDuty?.status === 'completed') {
      if (assignedDuty.ownerId && !existingRating) {
        setShowRatingModal(true);
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }, [assignedDuty, existingRating]);

  // Get substitute platform fee status
  const feeStatus = useQuery(api.app.billing.getSubstitutePlatformFeeStatus as any, isAssigned ? { dutyId } : 'skip') as SubstituteFeeStatus | null | undefined;

  // Payment status polling for local development (DA-313)
  const pollPaymentStatus = useAction(api.app.payments.webhookActions.pollPaymentStatus as any);
  const [dismissedPaidBannerPaymentKey, setDismissedPaidBannerPaymentKey] = React.useState<string | null>(null);
  const hasCheckedPaymentRef = React.useRef(false);
  // Key dismissal on the stable Convex payments id (matches the provider route, which keys on
  // servicePayment._id) rather than the external Mollie id. Fall back to the duty id only for a
  // paid fee with no linked payment record (legacy/manually-confirmed) so the banner still shows.
  const paidBannerPaymentKey = feeStatus?.paymentId ?? (feeStatus?.status === 'paid' ? String(dutyId) : null);
  const isPlatformFeePaid = feeStatus?.status === 'paid';
  const showPaidBanner = isPlatformFeePaid && paidBannerPaymentKey !== null && dismissedPaidBannerPaymentKey !== paidBannerPaymentKey;

  React.useEffect(() => {
    if (duty?.status !== 'filled') return;
    if (!feeStatus?.molliePaymentId) return;
    if (feeStatus?.status !== 'pending') return;
    if (hasCheckedPaymentRef.current) return;

    hasCheckedPaymentRef.current = true;
    pollPaymentStatus({ molliePaymentId: feeStatus.molliePaymentId }).catch(console.error);
  }, [duty?.status, feeStatus?.molliePaymentId, feeStatus?.status, pollPaymentStatus]);

  // Get documents for validation
  const loadedDocuments = useQuery(api.app.documents.queries.listDocumentsByDuty as any, isAssigned ? { dutyId } : 'skip') as SubstituteDocument[] | undefined;
  const documents = loadedDocuments ?? [];

  // DA-314: Fetch work invoice for this duty (if exists)
  const workInvoice = useQuery(api.app.operatorAccounting.getWorkInvoiceByDuty as any, isAssigned ? { dutyId } : 'skip') as WorkInvoiceCardData | null | undefined;

  // DA-314: Edit patient count dialog state
  const [isEditPatientCountDialogOpen, setIsEditPatientCountDialogOpen] = React.useState(false);
  const [editingDocument, setEditingDocument] = React.useState<SubstituteDocument | null>(null);

  // DA-315: Payment confirmation state
  const [confirmingPayment, setConfirmingPayment] = React.useState(false);
  const confirmManualPayment = useMutation(api.app.billing.confirmManualPaymentReceived as any);

  const [text, setText] = React.useState('');
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const me = useQuery(api.app.users.queries.getMe as any, {}) as SubstituteCurrentUser | undefined;
  const myUserId = me?._id ?? null;
  const canLoadMore = pagination?.status === 'CanLoadMore' && typeof pagination?.loadMore === 'function';

  // Handlers
  const onSend = async () => {
    if (!text.trim()) return;
    await send({ dutyId, content: text });
    setText('');
    await markRead({ dutyId });
  };

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await uploadChatAttachment({
        dutyId,
        file,
        createUploadUrl,
        finalizeAttachment: sendAttachment,
        markRead,
        onMarkReadError: (error) => console.error('Failed to update chat read marker:', error),
      });
      toast.success(t('duties:detail.fileUploadSuccess'));
    } catch (error) {
      console.error('File upload error:', error);
      toast.error(error instanceof Error ? error.message : t('duties:detail.fileUploadError'));
    } finally {
      e.target.value = '';
    }
  };

  const handlePickFile = () => fileInputRef.current?.click();

  const handleCompleteService = async () => {
    setIsCompleting(true);
    try {
      await completeService({ dutyId });
    } catch (err: any) {
      let errorMsg = t('duties:detail.completeServiceError');
      if (err?.message) {
        const match = err.message.match(/Uncaught Error: (.+?)(?:\n|$)/);
        if (match) {
          errorMsg = match[1];
        } else if (err.message.includes('All necessary documents')) {
          errorMsg = t('duties:detail.documentsRequiredError');
        } else {
          errorMsg = err.message;
        }
      }
      setWarningMessage(errorMsg);
      setShowWarningDialog(true);
    } finally {
      setIsCompleting(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!workInvoice) return;
    setConfirmingPayment(true);
    try {
      await confirmManualPayment({ workInvoiceId: workInvoice._id });
      toast.success(t('duties:detail.paymentConfirmedSuccess'));
    } catch (err: any) {
      console.error('Failed to confirm payment:', err);
      toast.error(err?.message || t('duties:detail.paymentConfirmError'));
    } finally {
      setConfirmingPayment(false);
    }
  };

  // If duty doesn't exist at all
  if (!duty) {
    return (
      <div className='max-w-6xl mx-auto p-4'>
        <div className='text-sm text-muted-foreground'>{t('duties:detail.notFound')}</div>
      </div>
    );
  }

  // If user is not assigned, show public view with apply button
  if (!isAssigned) {
    return <PublicDutyView duty={duty} hasApplied={hasApplied} myApplication={myApplication} canApplyResult={canApplyResult ?? { canApply: true }} openApply={openApply} focusRenew={focusRenew} />;
  }

  // Calculate derived values
  // At this point, duty is AssignedDutyData since we returned early if !isAssigned
  const assignedData = duty as AssignedDutyData;
  const hours = (duty.endDate - duty.startDate) / (1000 * 60 * 60);
  // DA-456: Use agreedHourlyRate from accepted application if available
  const hourlyRate = assignedData.agreedHourlyRate || duty.maxPrice || duty.minPrice || 0;
  const totalCompensation = hours * hourlyRate;

  // Calculate distance info
  const getDistanceInfo = (): DistanceInfo | null => {
    if (!myProfile?.location?.coordinates || !duty?.location?.coordinates) return null;
    const [myLat, myLon] = myProfile.location.coordinates;
    const [dutyLat, dutyLon] = duty.location.coordinates;

    if (typeof myLat !== 'number' || typeof myLon !== 'number' || typeof dutyLat !== 'number' || typeof dutyLon !== 'number') return null;

    const R = 6371;
    const dLat = ((dutyLat - myLat) * Math.PI) / 180;
    const dLon = ((dutyLon - myLon) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos((myLat * Math.PI) / 180) * Math.cos((dutyLat * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distanceKm = R * c;

    if (distanceKm < 0.1) return null;

    return { distanceKm: Math.round(distanceKm), travelTimeMin: Math.round((distanceKm / 60) * 60) };
  };

  const distanceInfo = getDistanceInfo();

  const googleMapsUrl = duty?.location?.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(duty.location.address)}`
    : duty?.location?.coordinates
      ? `https://www.google.com/maps/search/?api=1&query=${duty.location.coordinates[0]},${duty.location.coordinates[1]}`
      : null;

  // Status checks
  const isPendingContract = duty.status === 'pending_contract';
  const canComplete = duty.status === 'filled';
  const isPerformed = duty.status === 'performed';
  const isCompleted = duty.status === 'completed';
  const needsActivityReport = isAssigned && (isPerformed || isCompleted) && (!assignedData.activityReport || !!assignedData.activityReportRejectedAt);
  const requiredDocuments = requiredSettlementDocuments(duty.hasNotfallscheinbeteiligung, assignedData.activityReport?.kassenpatienten);
  const needsDocuments = isAssigned && isPerformed && !!assignedData.activityReport && !assignedData.activityReportConfirmedAt && !assignedData.activityReportRejectedAt && loadedDocuments !== undefined && documents.length < requiredDocuments;

  // Determine view status for hero and stepper
  const getViewStatus = (): SubstituteViewStatus => {
    if (duty.status === 'completed') return 'completed';
    if (duty.status === 'performed') return 'performed';
    return 'filled';
  };

  // Provider info for the card - prioritize duty.owner which contains full provider data
  const providerInfo =
    duty.owner ??
    duty.providerInfo ??
    (() => {
      // Build provider info object only including defined properties (exactOptionalPropertyTypes)
      const info: Record<string, string> = {};
      const firstName = duty.providerName?.split(' ')[0];
      const lastName = duty.providerName?.split(' ').slice(1).join(' ');
      if (firstName) info['firstName'] = firstName;
      if (lastName) info['lastName'] = lastName;
      if (duty.providerName) info['name'] = duty.providerName;
      if (duty.providerEmail) info['email'] = duty.providerEmail;
      if (duty.providerPhone) info['phone'] = duty.providerPhone;
      return info;
    })();

  // Chat content - reused in desktop and mobile (NOT a component to avoid remounting on state change)
  const chatContent = (
    <>
      {!chatOpen ? (
        <div className='h-[400px] lg:h-[50vh] flex flex-col items-center justify-center text-center gap-2'>
          <MessageSquare className='size-6 opacity-60' />
          <div className='text-sm font-medium'>{t('duties:detail.chatLockedTitle')}</div>
          <div className='text-xs text-muted-foreground'>{t('duties:detail.chatLockedDesc')}</div>
        </div>
      ) : (
        <div className='border rounded h-[400px] lg:h-[50vh] bg-muted/10'>
          <Conversation className='h-full'>
            <ConversationContent className='space-y-2'>
              {canLoadMore && (
                <div className='flex justify-center pb-2'>
                  <Button variant='secondary' size='sm' onClick={() => pagination?.loadMore?.(20)}>
                    {t('duties:detail.loadMore')}
                  </Button>
                </div>
              )}

              <div className='h-[calc(100%-4rem)]'>
                <ErrorBoundary onError={(err: Error) => console.debug('Chat disabled due to error:', err.message)}>
                  <MessagesList serviceId={dutyId} currentUserId={myUserId ?? ''} enabled={!!chatOpen} />
                </ErrorBoundary>
              </div>
            </ConversationContent>
          </Conversation>
        </div>
      )}

      <div className='mt-3'>
        {isCompleted ? (
          <div className='p-3 border border-muted rounded-md bg-muted/5 text-center'>
            <div className='text-sm text-muted-foreground'>{t('duties:detail.chatArchived')}</div>
          </div>
        ) : (
          <>
            <ConversationComposer value={text} onChange={setText} onSend={onSend} onPickFile={handlePickFile} disabled={!chatOpen} placeholder={t('duties:detail.writeMessage')} />
            <input ref={fileInputRef} type='file' accept='image/*,.pdf' className='hidden' onChange={onFileChange} />
          </>
        )}
      </div>
    </>
  );

  return (
    <div className='max-w-6xl mx-auto p-4 space-y-6'>
      {/* Page Hero */}
      <SubstitutePageHero title={duty.title} {...(duty.specialty ? { specialty: duty.specialty } : {})} status={getViewStatus()} serviceType={duty.serviceType} backHref='/substitute/applications' backLabel='Meine Bewerbungen' />

      {/* Progress Stepper */}
      <SubstituteProgressStepper currentStatus={getViewStatus()} className='py-2' />

      {/* Payment success banner — DA-443: persistent with close button */}
      {showPaidBanner && (
        <div className='rounded-lg border border-success/30 bg-success/10 p-4 animate-in fade-in slide-in-from-top-2 duration-500'>
          <div className='flex items-center gap-3'>
            <CheckCircle2 className='h-6 w-6 text-success flex-shrink-0' />
            <div className='flex-1'>
              <div className='text-sm font-semibold text-foreground'>{t('duties:detail.paymentSuccessTitle')}</div>
              <div className='text-xs text-muted-foreground'>{t('duties:detail.paymentSuccessDesc')}</div>
            </div>
            <Button variant='ghost' size='sm' className='h-7 w-7 p-0 flex-shrink-0' onClick={() => setDismissedPaidBannerPaymentKey(paidBannerPaymentKey)} aria-label='Schließen'>
              <X className='h-4 w-4' />
            </Button>
          </div>
        </div>
      )}

      {needsDocuments && (
        <DocumentsTaskCard
          hasNotfallscheinbeteiligung={duty.hasNotfallscheinbeteiligung ?? false}
          uploaded={documents.length}
          required={requiredDocuments}
          onUpload={() => {
            document.getElementById('documents')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            document.getElementById('documents-upload-trigger')?.focus({ preventScroll: true });
          }}
        />
      )}

      {/* Pending Contract Notice - waiting for provider to accept */}
      {isPendingContract && (
        <div className='rounded-lg border border-info/30 bg-info/10 p-4'>
          <div className='flex items-center gap-3'>
            <Clock className='h-6 w-6 text-info flex-shrink-0' />
            <div className='flex-1'>
              <div className='text-sm font-semibold text-foreground'>{t('duties:detail.pendingContractTitle')}</div>
              <div className='text-xs text-muted-foreground'>{t('duties:detail.pendingContractDesc')}</div>
            </div>
          </div>
        </div>
      )}

      {/* Rating Section (after completion) */}
      {isCompleted && ratingCheck && (
        <SectionCard elevated>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <div className='flex items-center justify-center size-10 rounded-full bg-warning/10'>
                <Star className='h-5 w-5 text-warning' />
              </div>
              <div>
                <div className='text-sm font-semibold'>{t('duties:detail.rateProvider')}</div>
                {existingRating ? (
                  <div className='text-xs text-muted-foreground'>{t('duties:detail.youRated', { rating: existingRating.rating, suffix: existingRating.rating !== 1 ? 'e' : '' })}</div>
                ) : (
                  <div className='text-xs text-muted-foreground'>{t('duties:detail.shareExperience')}</div>
                )}
              </div>
            </div>
            {ratingCheck.canRate && !existingRating ? (
              <Button size='sm' onClick={() => setShowRatingModal(true)}>
                {t('duties:detail.rateNow')}
              </Button>
            ) : (
              existingRating && <div className='text-sm text-muted-foreground'>{t('duties:detail.alreadyRated')}</div>
            )}
          </div>
        </SectionCard>
      )}

      {/* Main Content Grid */}
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* Left Column (2 cols on lg) */}
        <div className='lg:col-span-2 space-y-6'>
          {/* Provider Info Card */}
          <ProviderInfoCard provider={providerInfo} showContactInfo={true} />

          {/* Service Details */}
          <ServiceDetailsCard duty={duty} myApplication={myApplication} distanceInfo={distanceInfo} googleMapsUrl={googleMapsUrl} hours={hours} hourlyRate={hourlyRate} totalCompensation={totalCompensation} fmt={fmt} />

          {(isPendingContract || !!contract) && <ContractViewSection dutyId={id} />}

          {/* Activity Report Section - show after substitute has submitted */}
          {assignedData.activityReport && (
            <ActivityReportSection
              activityReport={assignedData.activityReport}
              hasNotfallscheinbeteiligung={duty.hasNotfallscheinbeteiligung ?? false}
              {...(duty.notfallscheinbeteiligungRate !== undefined && { notfallscheinbeteiligungRate: duty.notfallscheinbeteiligungRate })}
              isConfirmed={!!assignedData.activityReportConfirmedAt}
              {...(assignedData.activityReportConfirmedAt !== undefined && { confirmedAt: assignedData.activityReportConfirmedAt })}
            />
          )}

          {needsActivityReport && (
            <ActivityReportActionCard
              rejected={!!assignedData.activityReportRejectedAt}
              {...(assignedData.activityReportRejectionReason ? { rejectionReason: assignedData.activityReportRejectionReason } : {})}
              auditOnly={isCompleted}
              onSubmit={() => setShowActivityReportModal(true)}
            />
          )}

          {/* Platform Fee Payment Button - only show after activity report submitted AND documents uploaded */}
          {canComplete && assignedData.activityReport && documents && documents.length > 0 && <PlatformFeePaymentButton dutyId={dutyId} />}

          {/* Documents Section or Completed Summary */}
          {isCompleted ? (
            <CompletedDutySection duty={duty} dutyId={dutyId} fmt={fmt} />
          ) : (
            <div className='space-y-4'>
              <DocumentsSection serviceId={dutyId} dutyStatus={duty.status} hasNotfallscheinbeteiligung={duty.hasNotfallscheinbeteiligung ?? false} {...(needsDocuments && { requiredDocuments })} />

              {/* Work Invoice Section */}
              {isPerformed && (
                <SectionCard title={t('duties:detail.invoice')} elevated>
                  {workInvoice ? (
                    <div className='space-y-4'>
                      <WorkInvoiceCard invoice={workInvoice} viewerRole='substitute' />

                      {/* Bank Transfer Payment Confirmation Button */}
                      {workInvoice.paymentMethod === 'bank_transfer' && workInvoice.status === 'issued' && (
                        <div className='p-4 rounded-lg border-2 border-primary/30 bg-primary/5'>
                          <div className='text-sm text-muted-foreground mb-3'>
                            <strong>Hinweis:</strong> {t('duties:detail.bankTransferNote')}
                          </div>
                          <Button className='w-full' onClick={handleConfirmPayment} disabled={confirmingPayment}>
                            {confirmingPayment ? (
                              <>
                                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                {t('duties:detail.confirming')}
                              </>
                            ) : (
                              <>
                                <Check className='mr-2 h-4 w-4' />
                                {t('duties:detail.confirmPayment')}
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                      {workInvoice.paymentMethod === 'unknown' && <UnavailablePaymentMethodNotice />}
                    </div>
                  ) : (
                    <div className='text-center py-4'>
                      <div className='text-sm text-muted-foreground'>{t('duties:detail.invoiceAutoDesc')}</div>
                      {duty.hasNotfallscheinbeteiligung && duty.notfallscheinbeteiligungRate && documents.length > 0 && documents.some((doc) => doc.patientCount !== undefined || doc.substituteEditedPatientCount !== undefined) && (
                        <div className='mt-4'>
                          <Button
                            variant='outline'
                            size='sm'
                            onClick={() => {
                              const docWithPatientCount = documents.find((doc) => doc.patientCount !== undefined || doc.substituteEditedPatientCount !== undefined);
                              if (docWithPatientCount) {
                                setEditingDocument(docWithPatientCount);
                                setIsEditPatientCountDialogOpen(true);
                              }
                            }}
                          >
                            {t('duties:detail.editPatientCount')}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </SectionCard>
              )}
            </div>
          )}
        </div>

        {/* Right Column - Chat (1 col on lg) */}
        <div className='hidden lg:block'>
          <div className='sticky top-4'>
            <SectionCard
              title={t('duties:detail.chat')}
              badge={
                chatOpen && typeof unread === 'number' && unread > 0 ? (
                  <Badge variant='destructive' className='text-xs'>
                    {unread}
                  </Badge>
                ) : !chatOpen ? (
                  <Badge variant='outline' className='text-xs flex items-center gap-1'>
                    <Lock className='size-3' /> {t('duties:detail.locked')}
                  </Badge>
                ) : undefined
              }
              elevated
            >
              {chatContent}
            </SectionCard>
          </div>
        </div>
      </div>

      {/* Mobile Chat FAB — sits above the bottom nav (h-14) on phones; drops lower once the nav is hidden at md */}
      <div className='lg:hidden fixed bottom-20 right-6 z-50 md:bottom-6'>
        <Sheet open={mobileChatOpen} onOpenChange={setMobileChatOpen}>
          <SheetTrigger render={<Button size='lg' className='h-14 w-14 rounded-full shadow-lg relative' />}>
            <MessageSquare className='size-6' />
            {typeof unread === 'number' && unread > 0 && (
              <Badge variant='destructive' className='absolute -top-1 -right-1 size-5 p-0 flex items-center justify-center text-xs animate-pulse'>
                {unread > 9 ? '9+' : unread}
              </Badge>
            )}
          </SheetTrigger>
          <SheetContent side='bottom' className='h-[85vh]'>
            <SheetHeader>
              <SheetTitle className='flex items-center gap-2'>
                {t('duties:detail.chat')}
                {!chatOpen && (
                  <Badge variant='outline' className='text-xs'>
                    <Lock className='size-3 mr-1' /> {t('duties:detail.locked')}
                  </Badge>
                )}
              </SheetTitle>
            </SheetHeader>
            <div className='mt-4'>{chatContent}</div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Complete Service Button - sticky on mobile */}
      {canComplete && (
        <div className='sticky bottom-20 lg:bottom-4 bg-background/95 backdrop-blur border-t lg:border lg:rounded-lg p-4 -mx-4 lg:mx-0 shadow-lg'>
          <Button
            className='w-full'
            size='lg'
            onClick={async () => {
              // Step 1: Show ActivityReportModal first (if not submitted)
              if (!assignedData.activityReport) {
                setShowActivityReportModal(true);
                return;
              }
              // Step 2: Pay or waive the platform fee. Documents become available after
              // the fee webhook advances the duty to performed.
              const feeSatisfied = duty.substituteFeeStatus === 'not_required' || feeStatus?.status === 'paid';
              if (!feeSatisfied) {
                setShowFeeModal(true);
                return;
              }
              if (duty.status === 'filled') {
                try {
                  await markPerformed({ dutyId });
                  toast.success(t('duties:detail.serviceMarkedPerformed'));
                } catch (error) {
                  toast.error(error instanceof Error ? error.message : t('duties:detail.completeServiceError'));
                }
                return;
              }
              // Step 3: Legacy duties may already be performed, so validate documents
              // before calling the completion mutation when they are available.
              if (!documents || documents.length === 0) {
                toast.error(t('duties:detail.uploadDocumentsFirst'));
                return;
              }
              if (duty.hasNotfallscheinbeteiligung && assignedData.activityReport.kassenpatienten > 0 && documents.length < assignedData.activityReport.kassenpatienten) {
                toast.error(t('duties:detail.documentCountMismatch', { required: assignedData.activityReport.kassenpatienten, uploaded: documents.length }));
                return;
              }
              // Step 4: Complete service
              handleCompleteService();
            }}
            disabled={isCompleting}
          >
            {isCompleting ? t('duties:detail.processing') : t('duties:detail.completeService')}
          </Button>
        </div>
      )}

      {/* Modals */}
      {editingDocument && (
        <EditPatientCountDialog
          documentId={editingDocument._id}
          currentPatientCount={editingDocument.substituteEditedPatientCount ?? editingDocument.patientCount ?? 0}
          notfallscheinbeteiligungRate={duty.notfallscheinbeteiligungRate || 0}
          isOpen={isEditPatientCountDialogOpen}
          onClose={() => {
            setIsEditPatientCountDialogOpen(false);
            setEditingDocument(null);
          }}
          onSuccess={() => {}}
        />
      )}

      <Dialog open={showWarningDialog} onOpenChange={setShowWarningDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <AlertTriangle className='size-5 text-warning' />
              {t('duties:detail.warning')}
            </DialogTitle>
            <DialogDescription className='text-left pt-2'>{warningMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setShowWarningDialog(false)}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showFeeModal && (
        <SubstitutePlatformFeeModal
          dutyId={duty._id}
          onClose={() => setShowFeeModal(false)}
          onSuccess={() => {
            setShowFeeModal(false);
            toast.success(t('duties:detail.paymentProcessingStarted'));
          }}
        />
      )}

      {showActivityReportModal && (
        <ActivityReportModal
          dutyId={duty._id}
          auditOnly={isCompleted}
          hasNotfallscheinbeteiligung={duty.hasNotfallscheinbeteiligung ?? false}
          {...(duty.notfallscheinbeteiligungRate !== undefined && { notfallscheinbeteiligungRate: duty.notfallscheinbeteiligungRate })}
          onClose={() => setShowActivityReportModal(false)}
          onSuccess={() => {
            setShowActivityReportModal(false);
            toast.success(t('duties:activityReport.submitSuccess'));
            // After activity report, user needs to upload documents before paying fee
            // Show a hint if no documents uploaded yet
            if (!isCompleted && (!documents || documents.length === 0)) {
              toast.info(t('duties:detail.uploadDocumentsNext'));
            }
          }}
        />
      )}

      {showRatingModal && isCompleted && (
        <RateProviderModal
          isOpen={showRatingModal}
          onClose={() => setShowRatingModal(false)}
          onSuccess={() => toast.success(t('duties:detail.ratingThanks'))}
          dutyId={duty._id}
          providerName={duty.providerName || 'Anbieter'}
        />
      )}
    </div>
  );
};

// Lightweight apply modal
const ApplyModal: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  const [job, setJob] = React.useState<ApplyModalJob | null>(null);
  const [priceValue, setPriceValue] = React.useState(45);
  const [validityDays, setValidityDays] = React.useState<ChoosableApplicationValidityDays>(DEFAULT_APPLICATION_VALIDITY_DAYS);
  const [error, setError] = React.useState<string | null>(null);
  const { t } = useTranslation(['duties', 'common']);

  React.useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ApplyModalJob>).detail;
      setJob(detail ?? null);
      const initialValue = detail?.payment?.min ?? 45;
      setPriceValue(initialValue);
      setValidityDays(DEFAULT_APPLICATION_VALIDITY_DAYS);
      setError(null);
      setOpen(true);
    };

    window.addEventListener('openApplyModal', handler as EventListener);
    return () => window.removeEventListener('openApplyModal', handler as EventListener);
  }, []);

  if (!open || !job) return null;

  const min = job?.payment?.min ?? 45;
  const max = job?.payment?.max ?? 200;
  const isFixedPrice = job?.compensationType === 'fixed';

  const validateAndSubmit = () => {
    setError(null);

    if (!isFixedPrice) {
      if (priceValue < min) {
        setError(t('duties:apply.errorMin', { min }));
        return;
      }
      if (priceValue > max) {
        setError(t('duties:apply.errorMax', { max }));
        return;
      }
    }

    const jobId = job?.id ?? job?._id ?? null;
    const eventDetail: ApplyForJobEventDetail = {
      jobId: jobId as string,
      proposedCompensation: isFixedPrice ? min : priceValue,
      validityDays, // DA-434: Include validity period
      serviceType: job?.serviceType,
      compensationType: job?.compensationType,
      source: 'service_detail',
    };
    window.dispatchEvent(new CustomEvent('applyForJob', { detail: eventDetail }));

    setOpen(false);
    setJob(null);
  };

  const startMs = job?.startDate ? new Date(job.startDate).getTime() : null;
  const endMs = job?.endDate ? new Date(job.endDate).getTime() : null;
  const hours = startMs && endMs && endMs > startMs ? (endMs - startMs) / 3600000 : null;

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) {
          setOpen(false);
          setJob(null);
        }
      }}
    >
      <DialogContent className='max-w-lg'>
        <DialogHeader>
          <DialogTitle>{t('duties:apply.modalTitle', { title: job.title ?? 'Dienst' })}</DialogTitle>
          <DialogDescription className='sr-only'>{t('duties:apply.yourPriceProposal')}</DialogDescription>
        </DialogHeader>

        {isFixedPrice ? (
          <>
            <p className='mb-4 text-sm text-muted-foreground'>{t('duties:apply.fixedCompensation')}</p>
            <div className='mb-4 p-4 rounded-lg bg-muted/30'>
              <div className='text-center'>
                <div className='text-3xl font-bold text-foreground mb-1'>
                  {min}
                  {t('duties:apply.perHour')}
                </div>
                {hours && <div className='text-lg text-muted-foreground'>{t('duties:apply.totalCompensation', { amount: (min * hours).toFixed(2), hours: hours.toFixed(2) })}</div>}
              </div>
            </div>
            {job?.hasNotfallscheinbeteiligung && job?.notfallscheinbeteiligungRate && (
              <div className='mb-4 p-3 rounded-md bg-primary/5 border border-primary/20'>
                <div className='text-sm'>
                  <span className='font-medium text-primary'>{t('duties:apply.notfallscheinbeteiligung', { rate: job.notfallscheinbeteiligungRate })}</span>
                  <span className='text-muted-foreground'> {t('duties:apply.notfallscheinbeteiligungLabel')}</span>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <p className='mb-4 text-sm text-muted-foreground'>
              {min != null && <span>Min: {min}€/Std. </span>}
              {max != null && <span>Max: {max}€/Std.</span>}
            </p>
            <p className='mb-2 text-sm font-medium'>{t('duties:apply.yourPriceProposal')}</p>
            <div className='w-full flex items-center justify-between gap-4 mb-4'>
              <span className='text-sm text-muted-foreground'>{min} €</span>
              <div className='flex-1 relative'>
                <div
                  className='absolute inset-0 h-1.5 rounded-full pointer-events-none'
                  style={{
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background:
                      'linear-gradient(to right, oklch(0.65 0.2 30) 0%, oklch(0.68 0.18 35) 6%, oklch(0.72 0.16 45) 16%, oklch(0.75 0.14 55) 23%, oklch(0.78 0.12 70) 29%, oklch(0.80 0.11 90) 32%, oklch(0.85 0.14 140) 35%, oklch(0.88 0.16 148) 39%, oklch(0.88 0.16 148) 42%, oklch(0.85 0.14 145) 45%, oklch(0.80 0.12 140) 52%, oklch(0.75 0.09 135) 58%, oklch(0.68 0.07 160) 68%, oklch(0.62 0.05 180) 84%, oklch(0.6 0.05 200) 100%)',
                  }}
                />
                <Slider aria-label={t('duties:apply.yourPriceProposal')} value={[priceValue]} onValueChange={(values: number | readonly number[]) => setPriceValue(Array.isArray(values) ? values[0] : values)} min={min} max={max} step={5} />
              </div>
              <span className='text-sm text-muted-foreground'>{max} €</span>
            </div>
            <p className='text-center text-lg font-semibold mb-2'>{priceValue} € / Stunde</p>
            {priceValue > min + (max - min) * 0.8 && (
              <div className='mb-3 p-3 rounded-md bg-warning/10 border border-warning/30'>
                <div className='flex items-start gap-2'>
                  <AlertTriangle className='size-5 text-warning shrink-0 mt-0.5' />
                  <div className='text-sm text-warning'>{t('duties:apply.priceTooHigh')}</div>
                </div>
              </div>
            )}
            {hours && <p className='mt-2 text-sm text-muted-foreground text-center'>{t('duties:apply.totalCompensationBasis', { amount: (priceValue * hours).toFixed(2), hours: hours.toFixed(2) })}</p>}
            {job?.hasNotfallscheinbeteiligung && job?.notfallscheinbeteiligungRate && (
              <div className='mt-3 p-3 rounded-md bg-primary/5 border border-primary/20'>
                <div className='text-sm'>
                  <span className='font-medium text-primary'>{t('duties:apply.notfallscheinbeteiligung', { rate: job.notfallscheinbeteiligungRate })}</span>
                  <span className='text-muted-foreground'> {t('duties:apply.notfallscheinbeteiligungLabel')}</span>
                </div>
              </div>
            )}
          </>
        )}

        {error && <p className='mt-2 text-sm text-destructive'>{error}</p>}

        {/* DA-434: Validity period selector */}
        <div className='mt-4 border-t pt-4'>
          <p className='mb-2 text-sm font-medium'>{t('duties:apply.validityPeriod')}</p>
          <div className='flex gap-2'>
            {CHOOSABLE_APPLICATION_VALIDITY_DAYS.map((days) => (
              <Button key={days} type='button' variant={validityDays === days ? 'default' : 'outline'} size='sm' className='flex-1' onClick={() => setValidityDays(days)}>
                {days} {t('duties:apply.days')}
              </Button>
            ))}
          </div>
          <p className='mt-1.5 text-xs text-muted-foreground'>{t('duties:apply.validityHint')}</p>
        </div>

        <DialogFooter>
          <Button
            variant='ghost'
            onClick={() => {
              setOpen(false);
              setJob(null);
            }}
          >
            {t('duties:apply.cancel')}
          </Button>
          <Button onClick={validateAndSubmit}>{t('duties:apply.submit')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
